<template>
  <v-row class="row--35" align="start">
    <v-col lg="4" md="4" cols="12">
      <aside class="profile-side" v-reveal>
        <div class="thumbnail profile-photo" ref="photo" @mousemove="onPhotoMove" @mouseleave="onPhotoLeave">
          <div class="profile-photo__inner" :style="photoStyle">
            <slot name="thum-img"></slot>
            <div class="profile-photo__sheen" :style="sheenStyle"></div>
          </div>
        </div>

        <ul class="profile-facts">
          <li v-for="(fact, i) in facts" :key="i" v-reveal="120 + i * 60">
            <span class="profile-facts__icon" v-html="iconSvg(fact.icon)"></span>
            <span class="profile-facts__text">
              <a v-if="fact.href" :href="fact.href" target="_blank" rel="noopener" class="link im-hover">{{ fact.text }}</a>
              <template v-else>{{ fact.text }}</template>
            </span>
          </li>
        </ul>

        <!-- 桌面 / 平板：荣誉放在左栏个人卡里 -->
        <div class="profile-honors profile-honors--side" v-reveal="360">
          <span class="profile-honors__label">Honors</span>
          <div class="profile-honors__chips">
            <a v-for="(h, i) in honors" :key="i" :href="h.href" target="_blank" rel="noopener" class="honor-chip"
              :class="{ 'honor-chip--hl': h.highlight }">
              <span class="honor-chip__dot"></span>{{ h.text }}
            </a>
          </div>
        </div>
      </aside>
    </v-col>
    <v-col lg="8" md="8" cols="12">
      <div class="about-inner inner">
        <div class="section-title about-title">
          <span class="subtitle" v-reveal>Researcher &amp; Designer</span>
          <h2 class="heading-title" v-reveal="80">Zichen Tian</h2>
          <p class="description mt_dec--20" v-reveal="160">
            I am a PhD candidate in Artificial Intelligence (SMU, <a href="https://csrankings.org/#/index?ai&vision&mlmining&nlp&inforet&world"
              class="link im-hover" target="_blank">World-Rank #28 in AI Research</a>), honored to be advised by
            <a class="link im-hover" href="https://qianrusun.com/" target="_blank">Prof. SUN Qianru</a>. Prior to my
            doctoral studies, I was research associate at the
            <a class="link im-hover" href="https://www.ntu.edu.sg/s-lab/" target="_blank">S-Lab of Advanced
              Intelligence</a> (Nanyang Technological University) and at
            <a class="link im-hover" href="https://www.cs.tsinghua.edu.cn/csen/info/1309/4347.htm"
              target="_blank">Tsinghua University.</a>
          </p>
          <p class="research-line" v-reveal="240">
            <span class="research-line__bar"></span>
            I study how foundation models acquire new knowledge from scarce data, and how to predict adaptation failures
            before training. In synthetic aperture radar (SAR), multispectral and medical imaging, data lie far from the
            pretraining distribution and labels are few and long-tailed, so the pretrained prior is indispensable yet
            mismatched. I treat adaptation as <em>subspace allocation</em>: deciding, from the pretrained weights and
            target data, where and along which directions an update should act, reusing transferable structure rather
            than disrupting it. This line of work includes
            <a class="link im-hover" href="/deblora/">debLoRA</a> (NeurIPS 2024),
            <a class="link im-hover" href="/metapeft/">MetaPEFT</a> (CVPR 2025 Highlight) and
            <a class="link im-hover" href="/mtlora/">mtLoRA</a> (ICLR 2026).
          </p>
          <!-- 手机：荣誉放在自我介绍下面，语气更克制 -->
          <div class="profile-honors profile-honors--inline" v-reveal="280">
            <span class="profile-honors__label">Honors</span>
            <div class="profile-honors__chips">
              <a v-for="(h, i) in honors" :key="'m' + i" :href="h.href" target="_blank" rel="noopener"
                class="honor-chip" :class="{ 'honor-chip--hl': h.highlight }">
                <span class="honor-chip__dot"></span>{{ h.text }}
              </a>
            </div>
          </div>
        </div>
        <!-- End .section-title -->
        <div class="tab-wrapper mt--30" v-reveal="320">
          <v-row>
            <TabTwo />
          </v-row>
        </div>
      </div>
    </v-col>
    <img src="https://doem1997.goatcounter.com/count?p=mainpage" alt="" class="hidden-tracker" />
  </v-row>
</template>

<script>
import feather from "feather-icons";
import TabTwo from "../../components/tabs/TabTwo";
export default {
  components: { TabTwo },
  data() {
    return {
      tilt: { x: 0, y: 0, active: false },
      facts: [
        { icon: "book-open", text: "PhD in AI, SMU CVML Lab", href: "https://www.smu.edu.sg/" },
        { icon: "map-pin", text: "Singapore" },
        { icon: "mail", text: "doem1997 [AT] gmail [DOT] com", href: "mailto:doem1997@gmail.com" },
      ],
      honors: [
        { text: "Presidential Doctoral Fellowship 2026", href: "https://graduatestudies.smu.edu.sg/phd/research-achievements/presidential-doctoral-fellowship" },
        { text: "Presidential Doctoral Fellowship 2025", href: "https://graduatestudies.smu.edu.sg/phd/research-achievements/presidential-doctoral-fellowship" },
        { text: "Dean's List 2026", href: "https://computing.smu.edu.sg/phd/awards-achievements" },
        { text: "Dean's List 2025", href: "https://computing.smu.edu.sg/phd/awards-achievements" },
        { text: "CVPR'25 Highlight", href: "https://cvpr.thecvf.com/" },
      ],
    };
  },
  computed: {
    photoStyle() {
      const { x, y, active } = this.tilt;
      return {
        transform: active
          ? `perspective(900px) rotateX(${(-y * 6).toFixed(2)}deg) rotateY(${(x * 6).toFixed(2)}deg) scale(1.015)`
          : "perspective(900px) rotateX(0deg) rotateY(0deg) scale(1)",
      };
    },
    sheenStyle() {
      const { x, y, active } = this.tilt;
      return {
        opacity: active ? 1 : 0,
        background: `radial-gradient(circle at ${50 + x * 40}% ${50 + y * 40}%, rgba(255, 236, 210, 0.22) 0%, rgba(255, 236, 210, 0.06) 30%, transparent 60%)`,
      };
    },
  },
  methods: {
    iconSvg(icon) {
      return feather.icons[icon].toSvg({ width: 16, height: 16, "stroke-width": 1.75 });
    },
    onPhotoMove(e) {
      if (window.matchMedia("(hover: none)").matches) return;
      const r = this.$refs.photo.getBoundingClientRect();
      this.tilt = {
        x: (e.clientX - r.left) / r.width - 0.5,
        y: (e.clientY - r.top) / r.height - 0.5,
        active: true,
      };
    },
    onPhotoLeave() {
      this.tilt = { x: 0, y: 0, active: false };
    },
  },
};
</script>

<style scoped>
/* ---------- 左栏：照片 + 速览 + 荣誉（桌面端 sticky） ---------- */
.profile-side {
  position: sticky;
  top: 110px;
}

.profile-photo {
  perspective: 900px;
}

.profile-photo__inner {
  position: relative;
  border-radius: 18px;
  overflow: hidden;
  border: 1px solid rgba(212, 165, 116, 0.18);
  box-shadow: 0 30px 60px -30px rgba(0, 0, 0, 0.8), 0 0 0 1px rgba(255, 255, 255, 0.02) inset;
  transition: transform 0.45s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.45s ease;
  transform-style: preserve-3d;
  will-change: transform;
}

.profile-photo:hover .profile-photo__inner {
  box-shadow: 0 40px 80px -30px rgba(212, 165, 116, 0.25), 0 0 0 1px rgba(212, 165, 116, 0.25) inset;
}

.profile-photo__inner>>>img {
  display: block;
  width: 100%;
  border-radius: 0 !important;
}

.profile-photo__sheen {
  position: absolute;
  inset: 0;
  pointer-events: none;
  mix-blend-mode: screen;
  transition: opacity 0.4s ease;
}

.profile-facts {
  list-style: none;
  padding: 0;
  margin: 22px 0 0;
}

.profile-facts li {
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 7px 0;
  font-family: var(--font-primary);
  font-size: 0.95rem;
  line-height: 1.5;
  color: var(--color-body);
}

.profile-facts__icon {
  display: inline-flex;
  flex: 0 0 auto;
  color: var(--color-primary);
  opacity: 0.85;
}

.profile-facts__text {
  min-width: 0;
}

.profile-facts .link {
  font-size: 0.95rem;
  font-weight: 500;
}

.profile-honors {
  margin-top: 18px;
  padding-top: 18px;
  border-top: 1px solid rgba(212, 165, 116, 0.14);
}

/* 手机版副本默认隐藏，≤767px 时与左栏副本互换 */
.profile-honors--inline {
  display: none;
}

.profile-honors__label {
  display: block;
  font-family: var(--font-primary);
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 2.2px;
  text-transform: uppercase;
  color: var(--color-primary);
  opacity: 0.65;
  margin-bottom: 10px;
}

.profile-honors__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.honor-chip {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 5px 11px 5px 9px;
  border-radius: 50em;
  font-family: var(--font-primary);
  font-size: 0.8rem;
  font-weight: 500;
  color: var(--color-heading);
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  text-decoration: none;
  transition: border-color 0.25s ease, background 0.25s ease, transform 0.25s ease;
}

.honor-chip:hover {
  border-color: rgba(212, 165, 116, 0.5);
  background: rgba(212, 165, 116, 0.08);
  transform: translateY(-1px);
}

/* 重点荣誉：金色填充胶囊 */
.honor-chip--hl {
  color: #0A0908;
  font-weight: 600;
  background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-accent-muted) 100%);
  border-color: transparent;
  box-shadow: 0 8px 20px -8px rgba(212, 165, 116, 0.6);
}

.honor-chip--hl:hover {
  background: linear-gradient(135deg, var(--color-accent) 0%, var(--color-primary) 100%);
  border-color: transparent;
}

.honor-chip--hl .honor-chip__dot {
  background: #0A0908;
  box-shadow: none;
}

.honor-chip__dot {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: var(--color-primary);
  box-shadow: 0 0 8px rgba(212, 165, 116, 0.8);
}

/* ---------- 右栏：标题 + 简介 ---------- */
.description {
  font-weight: 400;
  font-size: 1.1rem !important;
  color: var(--color-body);
}

.description a {
  font-weight: 500 !important;
  font-size: 1.1rem !important;
}

.research-line {
  position: relative;
  margin: 22px 0 0;
  padding-left: 18px;
  font-family: var(--font-primary);
  font-size: 1.1rem;
  line-height: 1.7;
  color: var(--color-heading);
}

.research-line em {
  font-style: normal;
  color: var(--color-primary);
}

.research-line__bar {
  position: absolute;
  left: 0;
  top: 0.2em;
  bottom: 0.2em;
  width: 2px;
  border-radius: 2px;
  background: linear-gradient(180deg, var(--color-primary), transparent);
}

.hidden-tracker {
  display: none;
}

/* ---------- 平板 / 手机 ---------- */
@media (max-width: 991px) {
  .profile-side {
    position: static;
  }
}

@media (max-width: 767px) {
  .profile-photo__inner {
    border-radius: 16px;
  }

  .profile-photo__inner>>>img {
    aspect-ratio: 16 / 10;
    object-fit: cover;
    object-position: 60% 40%;
  }

  .profile-facts {
    margin-top: 16px;
  }

  .profile-facts li {
    font-size: 0.9rem;
    padding: 4px 0;
  }

  .profile-facts .link {
    font-size: 0.9rem;
  }

  .profile-honors--side {
    display: none;
  }

  .profile-honors--inline {
    display: block;
    margin-top: 22px;
    padding-top: 16px;
  }

  .about-inner {
    margin-top: 4px;
  }

  .description.mt_dec--20 {
    margin-top: 0 !important;
  }

  .description,
  .description a {
    font-size: 1.02rem !important;
    line-height: 1.7 !important;
  }

  .research-line {
    font-size: 1.02rem;
    margin-top: 16px;
  }
}
</style>
